export interface ReportOrganMapSpecimenData {
  id: string;
  organMapTemplateId?: string | null;
  organMapTemplateName?: string | null;
  organMapImageUrl?: string | null;
  organMapWidth?: number | null;
  organMapHeight?: number | null;
  subsiteName?: string | null;
  subsitePositionX?: number | null;
  subsitePositionY?: number | null;
}

interface ReportTemplateSectionData {
  type: string;
}

export function reportTemplateUsesOrganMaps(
  sections: ReportTemplateSectionData[]
): boolean {
  return sections.some(
    ({ type }) => type === "organ_map" || type === "diagnostic_map"
  );
}

export function hasMissingExpectedOrganMapImage(
  sections: ReportTemplateSectionData[],
  specimens: ReportOrganMapSpecimenData[]
): boolean {
  return (
    reportTemplateUsesOrganMaps(sections) &&
    specimens.some(
      ({ organMapTemplateId, organMapImageUrl }) =>
        Boolean(organMapTemplateId) && !organMapImageUrl
    )
  );
}

const mergeOrganMapData = <T extends ReportOrganMapSpecimenData>(
  live: T,
  authoritative: T
): T => {
  const sameTemplate =
    authoritative.organMapTemplateId === live.organMapTemplateId;

  return {
    ...live,
    organMapTemplateId: authoritative.organMapTemplateId,
    organMapTemplateName: authoritative.organMapTemplateName,
    organMapImageUrl:
      authoritative.organMapImageUrl ||
      (sameTemplate ? live.organMapImageUrl : authoritative.organMapImageUrl),
    organMapWidth:
      authoritative.organMapWidth ??
      (sameTemplate ? live.organMapWidth : authoritative.organMapWidth),
    organMapHeight:
      authoritative.organMapHeight ??
      (sameTemplate ? live.organMapHeight : authoritative.organMapHeight),
    subsitePositionX: authoritative.subsitePositionX,
    subsitePositionY: authoritative.subsitePositionY,
  };
};

export function mergeAuthoritativeOrganMapData<
  T extends ReportOrganMapSpecimenData
>(liveSpecimens: T[], authoritativeSpecimens: T[]): T[] {
  const authoritativeById = new Map(
    authoritativeSpecimens.map((specimen) => [specimen.id, specimen])
  );
  const mergedLiveSpecimens = liveSpecimens.map((specimen) => {
    const authoritative = authoritativeById.get(specimen.id);
    return authoritative
      ? mergeOrganMapData(specimen, authoritative)
      : specimen;
  });
  const liveSpecimenIds = new Set(liveSpecimens.map(({ id }) => id));
  const missingLiveSpecimens = authoritativeSpecimens.filter(
    ({ id }) => !liveSpecimenIds.has(id)
  );

  return [...mergedLiveSpecimens, ...missingLiveSpecimens];
}
